/**
 * InspectPopover — consolidated read/inspect popover for a non-SELECT-eligible
 * availability cell (sold, reserved-other, option-other, unavailable-window,
 * ASK_AVAILABILITY, or a stacked multi-entry cell).
 *
 * Part of the `unified-availability-interaction` capability (task 4.3) — retires
 * the bespoke `AvailabilityCellPopoverContent` two-popover path. Renders on top
 * of the shared `AvailabilityInspectPopoverContent` presentational shell (see
 * `./inspect-popover-parts.tsx` for the extracted sub-pieces).
 *
 * ASK_AVAILABILITY (`cellState === 'unknown'`) placement gap: the
 * `unified-availability-interaction` spec's SELECT/INSPECT eligibility lists
 * (`specs/unified-availability-interaction/spec.md:5,91`) never mention
 * ASK_AVAILABILITY. Pragmatic resolution (per
 * `openspec/changes/unify-availability-interaction/tasks/research-web.md`
 * Boundary Gotcha #4, confirmed acceptable by the task's own blocker note):
 * ASK_AVAILABILITY is INSPECT-eligible (it is not SELECT-eligible either), and
 * this branch preserves the retired component's enquiry + admin-override
 * affordance verbatim via `AskAvailabilityActions`.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AskAvailabilityActions } from '@ooh/ui/components/ask-availability-actions';
import type { CellEntry } from '@ooh/contracts';
import type { CellState } from '../lib/availability-types';
import { AvailabilityInspectPopoverContent } from './availability-inspect-popover-content';
import { resolvePopoverDateRangeLabel } from './availability-cell-visual';
import { useTtlCountdown } from '../lib/ttl-countdown';
import {
  InspectOccupiedCell,
  resolveDominantEntry,
  resolveOccupiedCellView,
  toInspectStatus,
} from './inspect-popover-parts';

export interface InspectPopoverProps {
  /** All coexisting entries for this cell — one for a plain occupied cell, >1 for a stacked cell. */
  entries: CellEntry[];
  /** Resolved dominant cell state — drives the ASK_AVAILABILITY / UNAVAILABLE-window branch split. */
  cellState: CellState;
  unitLabel: string;
  /**
   * Fallback date-range label for a cell with no reservation of its own — the
   * calendar period (ASK_AVAILABILITY) or the availability-window's own displayed
   * range (`cellState === 'unavailable'`). An occupied, reservation-backed entry
   * (SOFT/HARD/OPTION/SOLD) instead shows its OWN booking window
   * (`dominant.dateFrom`-`dominant.dateTo`), never this period-derived value — see
   * `resolvePopoverDateRangeLabel` and the `unified-availability-interaction`
   * INSPECT-popover requirement.
   */
  dateRangeLabel: string;
  mediaUnitId: number;
  /**
   * The inspected cell's own date range (the calendar period it belongs to, e.g.
   * `detailPeriod.start`/`.end`) — used only to gate the Anuluj/Wymuś zwolnienie
   * whole-versus-selected-period choice (CU-86cbh03pj INSPECT-popover parity).
   * `undefined` at a call site with no such data degrades to the pre-existing
   * whole-release-only behavior, same as `UnifiedSelectPopover`'s `holdDateFrom`/
   * `holdDateTo`.
   */
  inspectedCellDateFrom?: string;
  inspectedCellDateTo?: string;
  /**
   * Group gate for rezerwujący/klient/oferta — the caller has effective view on the
   * reservation's offer, or holds `offer.manage-any`. Independent of the four
   * quick-action transition permissions below — label authorization and
   * transition-permission authorization are independent gates (spec text).
   */
  hasLabelAuthorization: boolean;
  /** Fail-closed resolved permissions — caller resolves via useHasPermission(key, { loading: 'closed' }). */
  canUpgradeHard: boolean;
  canCloseSale: boolean;
  canCancel: boolean;
  canForceRelease: boolean;
  /** Waitlist data for the dominant entry — omit when there is no queue at all. */
  queueInfo?: { count: number; myPosition: number | null };
  /** Called once when the TTL countdown reaches zero. */
  onExpired?: () => void;
  onUpgradeHard?: () => void;
  onCloseSale?: () => void;
  onCancel?: () => void;
  onForceRelease?: () => void;
  /** Join/leave-queue handlers for the waitlist strip — omit to keep it read-only. */
  onJoinQueue?: () => void;
  onLeaveQueue?: () => void;
  /** See `AvailabilityInspectPopoverContentProps.queueSection` (CU-86cb7ewja). */
  queueSection?: React.ReactNode;
  // ── ASK_AVAILABILITY branch (cellState === 'unknown') — preserved verbatim ──
  canManage: boolean;
  enquireLabel: string;
  enquireHref: string;
  setStatusManuallyLabel: string;
  onSetStatusManually?: () => void;
  /** Base test id — combined with a suffix to keep ids unique per cell. */
  testId: string;
}

export function InspectPopover({
  entries,
  cellState,
  unitLabel,
  dateRangeLabel,
  mediaUnitId,
  inspectedCellDateFrom,
  inspectedCellDateTo,
  hasLabelAuthorization,
  canUpgradeHard,
  canCloseSale,
  canCancel,
  canForceRelease,
  queueInfo,
  onExpired,
  onUpgradeHard,
  onCloseSale,
  onCancel,
  onForceRelease,
  onJoinQueue,
  onLeaveQueue,
  queueSection,
  canManage,
  enquireLabel,
  enquireHref,
  setStatusManuallyLabel,
  onSetStatusManually,
  testId,
}: InspectPopoverProps) {
  const { dominant, secondary } = resolveDominantEntry(entries);
  // Deadline markers are shared, word-for-word, with the reservation-list
  // surfaces (reservation-panel spec + availability-calendar "the expiry
  // deadline reaches the operator through the cell popover" requirement) —
  // sourced from the same `reservations:deadline.*` keys instead of a
  // duplicate inventory-namespace copy.
  const { t: tReservations } = useTranslation('reservations');

  // All hooks called unconditionally, before any early return (rules-of-hooks) —
  // ttlExpiresAt is memoized: useTtlCountdown's effect keys off this reference,
  // and a fresh `new Date(str)` on every render would retrigger the effect on
  // every tick (Date objects are never reference-equal for the same value),
  // causing an infinite render loop.
  const ttlExpiresAt = React.useMemo(
    () => (dominant?.ttlExpiresAt ? new Date(dominant.ttlExpiresAt) : null),
    [dominant?.ttlExpiresAt],
  );
  const remaining = useTtlCountdown(ttlExpiresAt, onExpired);

  if (cellState === 'unknown') {
    return (
      <AskAvailabilityActions
        containerTestId={`${testId}-ask-availability`}
        enquireLabel={enquireLabel}
        enquireHref={enquireHref}
        enquireLinkTestId={`${testId}-enquire-link`}
        canManage={canManage}
        setStatusManuallyLabel={setStatusManuallyLabel}
        setStatusManuallyTestId={`${testId}-set-status-manually-btn`}
        onSetStatusManually={onSetStatusManually}
      />
    );
  }

  if (cellState === 'unavailable') {
    return (
      <AvailabilityInspectPopoverContent
        status="blocked"
        unitLabel={unitLabel}
        dateRangeLabel={dateRangeLabel}
        actions={null}
      />
    );
  }

  const status = dominant ? toInspectStatus(dominant.status) : null;
  if (!dominant || !status) return null;

  const view = resolveOccupiedCellView(dominant, status, remaining, {
    hasLabelAuthorization,
    queueInfo,
    mediaUnitId,
    permissions: { canUpgradeHard, canCloseSale, canCancel, canForceRelease },
    deadlineLabels: {
      noDeadlineSet: tReservations('deadline.noDeadlineSet'),
      expiredMarker: tReservations('deadline.expiredMarker'),
    },
    inspectedCellDateFrom,
    inspectedCellDateTo,
  });

  return (
    <InspectOccupiedCell
      view={view}
      unitLabel={unitLabel}
      dateRangeLabel={resolvePopoverDateRangeLabel(dominant, dateRangeLabel)}
      secondary={secondary}
      hasLabelAuthorization={hasLabelAuthorization}
      queueInfo={queueInfo}
      onUpgradeHard={onUpgradeHard}
      onCloseSale={onCloseSale}
      onCancel={onCancel}
      onForceRelease={onForceRelease}
      onJoinQueue={onJoinQueue}
      onLeaveQueue={onLeaveQueue}
      queueSection={queueSection}
      testId={testId}
    />
  );
}
